/**
 * Dashboard page with pipeline overview and key metrics
 */

import React from "react";
import { Card, Row, Col, Statistic, Typography, List, Tag, Space } from "antd";
import {
  DollarOutlined,
  RiseOutlined,
  TeamOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useList } from "@refinedev/core";
import { useNavigate } from "react-router-dom";

const { Title, Text, Link } = Typography;

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  // Fetch pipeline summary
  const { data: opportunitiesData, isLoading } = useList({
    resource: "opportunities",
    pagination: { pageSize: 100 },
    sorters: [{ field: "closingdate", order: "asc" }],
  });

  const opportunities = opportunitiesData?.data || [];

  // Calculate metrics
  const totalPipeline = opportunities.reduce(
    (sum, opp) => sum + (Number(opp.amount) || 0),
    0
  );

  const openDeals = opportunities.filter(
    (opp) => !["Closed Won", "Closed Lost"].includes(opp.sales_stage)
  );

  // Filter won deals by current year
  const currentYear = new Date().getFullYear();
  const wonDealsThisYear = opportunities.filter((opp) => {
    if (opp.sales_stage !== "Closed Won") return false;
    if (!opp.closingdate) return false;
    const closingYear = new Date(opp.closingdate).getFullYear();
    return closingYear === currentYear;
  });

  const byStage: Record<string, typeof opportunities> = {};
  openDeals.forEach((opp) => {
    const stage = opp.sales_stage || "Unknown";
    if (!byStage[stage]) byStage[stage] = [];
    byStage[stage].push(opp);
  });

  return (
    <div>
      <Title
        level={4}
        style={{ marginBottom: 24, color: "#1a1a2e", fontWeight: 600 }}
      >
        Dashboard
      </Title>

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card
            hoverable
            onClick={() =>
              navigate(
                `/opportunities?filter=${encodeURIComponent(
                  "sales_stage NOT IN ('Closed Won', 'Closed Lost')"
                )}`
              )
            }
            style={{ cursor: "pointer" }}
          >
            <Statistic
              title="Total Pipeline"
              value={totalPipeline}
              prefix={<DollarOutlined />}
              precision={0}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() =>
              navigate(
                `/opportunities?filter=${encodeURIComponent(
                  "sales_stage NOT IN ('Closed Won', 'Closed Lost')"
                )}`
              )
            }
            style={{ cursor: "pointer" }}
          >
            <Statistic
              title="Open Deals"
              value={openDeals.length}
              prefix={<RiseOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card
            hoverable
            onClick={() =>
              navigate(
                `/opportunities?filter=${encodeURIComponent(
                  `sales_stage = 'Closed Won' AND closingdate >= '${currentYear}-01-01' AND closingdate <= '${currentYear}-12-31'`
                )}`
              )
            }
            style={{ cursor: "pointer" }}
          >
            <Statistic
              title={`Won in ${currentYear}`}
              value={wonDealsThisYear.length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: "#10b981" }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Deal Size"
              value={openDeals.length ? totalPipeline / openDeals.length : 0}
              prefix={<TeamOutlined />}
              precision={0}
            />
          </Card>
        </Col>
      </Row>

      {/* Pipeline Funnel */}
      <Card title="Pipeline Funnel" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            overflowX: "auto",
            paddingBottom: 8,
          }}
        >
          {Object.entries(byStage).map(([stage, deals]) => {
            const stageValue = deals.reduce(
              (sum, d) => sum + (Number(d.amount) || 0),
              0
            );
            return (
              <div
                key={stage}
                onClick={() =>
                  navigate(
                    `/opportunities?filter=${encodeURIComponent(
                      `sales_stage = '${stage}'`
                    )}`
                  )
                }
                style={{
                  flex: "1 0 140px",
                  minWidth: 140,
                  textAlign: "center",
                  padding: "12px 8px",
                  background: `${getStageColor(stage)}15`,
                  borderRadius: 8,
                  borderLeft: `4px solid ${getStageColorHex(stage)}`,
                  cursor: "pointer",
                  transition: "transform 0.15s, box-shadow 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Text strong style={{ fontSize: 13, display: "block" }}>
                  {stage}
                </Text>
                <div style={{ margin: "8px 0" }}>
                  <Text style={{ fontSize: 24, fontWeight: 600 }}>
                    {deals.length}
                  </Text>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {stageValue.toLocaleString()}
                </Text>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Hot Deals - Closing Soon */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="🔥 Closing This Month" size="small" loading={isLoading}>
            <List
              size="small"
              dataSource={openDeals
                .filter((d) => {
                  if (!d.closingdate) return false;
                  const closeDate = new Date(d.closingdate);
                  const now = new Date();
                  return (
                    closeDate.getMonth() === now.getMonth() &&
                    closeDate.getFullYear() === now.getFullYear()
                  );
                })
                .slice(0, 5)}
              renderItem={(deal) => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/opportunities/${deal.id}`)}
                >
                  <div style={{ width: "100%" }}>
                    <Link ellipsis style={{ display: "block" }}>
                      {deal.potentialname}
                    </Link>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {Number(deal.amount || 0).toLocaleString()}
                      {deal.closingdate && ` · ${deal.closingdate}`}
                    </Text>
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: "No deals closing this month" }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="⚡ Top Deals by Value" size="small" loading={isLoading}>
            <List
              size="small"
              dataSource={[...openDeals]
                .sort(
                  (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0)
                )
                .slice(0, 5)}
              renderItem={(deal) => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/opportunities/${deal.id}`)}
                >
                  <div style={{ width: "100%" }}>
                    <Link ellipsis style={{ display: "block" }}>
                      {deal.potentialname}
                    </Link>
                    <Space>
                      <Tag color={getStageColor(deal.sales_stage)}>
                        {deal.sales_stage}
                      </Tag>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {Number(deal.amount || 0).toLocaleString()}
                      </Text>
                    </Space>
                  </div>
                </List.Item>
              )}
              locale={{ emptyText: "No open deals" }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

const getStageColor = (stage: string): string => {
  const colors: Record<string, string> = {
    Prospecting: "blue",
    Qualification: "cyan",
    "Needs Analysis": "geekblue",
    "Value Proposition": "purple",
    Negotiation: "orange",
    "Closed Won": "green",
    "Closed Lost": "red",
  };
  return colors[stage] || "default";
};

const getStageColorHex = (stage: string): string => {
  const colors: Record<string, string> = {
    Prospecting: "#6366f1",
    Qualification: "#0ea5e9",
    "Needs Analysis": "#3b82f6",
    "Value Proposition": "#8b5cf6",
    Negotiation: "#f59e0b",
    "Closed Won": "#10b981",
    "Closed Lost": "#ef4444",
  };
  return colors[stage] || "#94a3b8";
};

export default Dashboard;
